/**
 * Sidebar notifications bell.
 *
 * Rendered in the sidebar footer when the admin has enabled the
 * ``notification_icon_side`` placement (Platform Admin → Features →
 * Deliveries & Notifications). Links to the full notifications page and shows
 * an unread badge. The top-bar bell is controlled independently — both may be
 * shown at once ("keep both").
 *
 * @link TopBar for the top-bar bell + popover.
 */
import { Link, useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';

import { useNotificationUnread } from '../notifications';
import { cn } from '../../lib/utils';

interface SidebarNotificationBellProps {
  /** Sidebar collapsed state — hides the label + shrinks the icon margin. */
  collapsed: boolean;
  /** Close the mobile drawer on navigation. */
  onNavigate?: () => void;
}

/**
 * Notifications entry for the sidebar footer (unread-aware, links to
 * ``/notifications``). The unread count comes from the shared
 * {@link useNotificationUnread} hook, which keeps the same 60s cadence and the
 * same ``notifications-updated`` refresh the top-bar bell uses — so the two
 * badges can never disagree — and excludes ``background``-tier rows.
 */
export default function SidebarNotificationBell({ collapsed, onNavigate }: SidebarNotificationBellProps) {
  const location = useLocation();
  const { unreadCount } = useNotificationUnread();
  const active = location.pathname === '/notifications';

  return (
    <Link
      to="/notifications"
      onClick={() => {
        if (window.innerWidth < 768) onNavigate?.();
      }}
      className={cn(
        'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
        active
          ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
      )}
      aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
    >
      <span className="relative flex-shrink-0">
        <Bell className={cn(
          'h-5 w-5 transition-all duration-200',
          collapsed ? 'mr-0' : 'mr-3',
          active ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
        )} />
        {/* Collapsed rail has no room for the numeric badge, so a dot marks
            unread; expanded shows the count pill beside the label instead. */}
        {unreadCount > 0 && collapsed && (
          <span
            className="absolute -top-1 -right-0.5 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-card"
            aria-hidden="true"
          />
        )}
      </span>
      {!collapsed && (
        <span className={cn(
          'font-medium transition-all duration-200',
          active ? 'text-primary font-semibold' : 'group-hover:text-primary/70'
        )}>
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
      )}
    </Link>
  );
}
